/**
 * admin/src/pages/NotificationsPage.tsx
 * Gửi email hàng loạt và xem lịch sử
 */

import { useState } from 'react';
import { useNotifications, useNotifMutations } from '../hooks/useNotifications';
import {
  DataTable,
  Badge,
  Pagination,
  ErrorBanner,
  formatDateTime,
  Modal,
} from '../components/ui/index';
import type { AdminNotificationLog, NotificationTarget } from '../types/admin';
import type { Column } from '../components/ui/index';

type NotifRow = AdminNotificationLog & Record<string, unknown>;

// Estimated recipient counts by target (display hint)
const TARGET_LABELS: Record<NotificationTarget, string> = {
  all: 'Tất cả users',
  free: 'Free tier',
  basic: 'Basic tier',
  premium: 'Premium tier',
  specific: 'Email cụ thể',
};

const TARGET_BADGE_MAP: Record<NotificationTarget, 'info' | 'free' | 'basic' | 'premium' | 'warning'> = {
  all: 'info',
  free: 'free',
  basic: 'basic',
  premium: 'premium',
  specific: 'warning',
};

export function NotificationsPage() {
  // ─── Compose form state ───────────────────────────────────────────────────
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState<NotificationTarget>('all');
  const [specificEmail, setSpecificEmail] = useState('');
  const [limitSend, setLimitSend] = useState(500);
  const [formError, setFormError] = useState('');
  const [emailMode, setEmailMode] = useState<'plain' | 'html'>('plain');
  const [showInlinePreview, setShowInlinePreview] = useState(false);

  // ─── Pagination ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { logs, total, totalPages, isLoading: notifLoading, error, refetch } = useNotifications(page, 20);
  const { sendNotification, isSending, lastResult } = useNotifMutations(() => {
    refetch();
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────
  function handleOpenCompose() {
    setSubject('');
    setBody('');
    setTarget('all');
    setSpecificEmail('');
    setLimitSend(500);
    setFormError('');
    setShowComposeModal(true);
  }

  function handlePreview() {
    if (!subject.trim() || !body.trim()) {
      setFormError('Nhập subject và body trước khi xem preview');
      return;
    }
    setShowInlinePreview(true);
  }

  async function handleSend() {
    if (!subject.trim()) {
      setFormError('Subject là bắt buộc');
      return;
    }
    if (!body.trim()) {
      setFormError('Body email là bắt buộc');
      return;
    }
    if (target === 'specific' && !specificEmail.trim()) {
      setFormError('Vui lòng nhập địa chỉ email');
      return;
    }
    setFormError('');
    const result = await sendNotification({
      subject: subject.trim(),
      body: body.trim(),
      target,
      specific_email: target === 'specific' ? specificEmail.trim() : undefined,
      limit_send: limitSend,
    });
    if (result) {
      setShowComposeModal(false);
    }
  }

  // ─── Table columns ────────────────────────────────────────────────────────────────
  const notifRows = logs as NotifRow[];
  const columns: Column<NotifRow>[] = [
    {
      key: 'subject',
      label: 'Subject',
      render: (row) => <span className="cell-primary">{row.subject as string}</span>,
    },
    {
      key: 'target',
      label: 'Target',
      render: (row) => (
        <Badge variant={TARGET_BADGE_MAP[row.target as NotificationTarget] ?? 'info'}>
          {TARGET_LABELS[row.target as NotificationTarget] ?? (row.target as string)}
        </Badge>
      ),
    },
    {
      key: 'sent_count',
      label: 'Gửi thành công',
      render: (row) => (
        <span style={{ color: 'var(--text-1)' }}>
          {(row.sent_count as number).toLocaleString('vi-VN')} ✅
        </span>
      ),
    },
    {
      key: 'fail_count',
      label: 'Lỗi',
      render: (row) =>
        (row.fail_count as number) > 0 ? (
          <Badge variant="danger">{row.fail_count as number} ❌</Badge>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>0</span>
        ),
    },
    {
      key: 'sent_at',
      label: 'Thời gian',
      render: (row) => formatDateTime(row.sent_at as number),
    },
    {
      key: 'sent_by',
      label: 'Gửi bởi',
      render: (row) => <span className="cell-muted">{row.sent_by as string}</span>,
    },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Thông báo Email</h1>
          <p>Gửi email hàng loạt đến users</p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenCompose}>
          ✉️ Soạn thông báo
        </button>
      </div>

      {/* Send result card */}
      {lastResult && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            display: 'flex',
            gap: 24,
            alignItems: 'center',
            padding: '16px 20px',
            borderLeft: '3px solid var(--accent)',
          }}
        >
          <span style={{ fontSize: 20 }}>📊</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>
              Kết quả gửi vừa xong
            </div>
            <div style={{ display: 'flex', gap: 20, fontSize: 13, color: 'var(--text-2)' }}>
              <span>
                Tổng: <strong style={{ color: 'var(--text-1)' }}>{lastResult.total}</strong>
              </span>
              <span>
                Thành công: <strong style={{ color: '#4ade80' }}>{lastResult.sent_count} ✅</strong>
              </span>
              {lastResult.fail_count > 0 && (
                <span>
                  Thất bại: <strong style={{ color: '#f87171' }}>{lastResult.fail_count} ❌</strong>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {/* History table */}
      <div className="card">
        <div className="table-toolbar">
          <div className="table-title">Lịch sử gửi — {total.toLocaleString('vi-VN')} bản ghi</div>
        </div>
        <div className="table-wrapper">
          <DataTable<NotifRow>
            columns={columns}
            rows={notifRows}
            isLoading={notifLoading}
            emptyText="Chưa có email nào được gửi"
          />
        </div>
        <Pagination
          page={page}
          totalPages={totalPages}
          totalRows={total}
          pageSize={20}
          onPageChange={setPage}
          hasPrev={page > 1}
          hasNext={page < totalPages}
        />
      </div>

      {/* ─── Compose Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={showComposeModal}
        onClose={() => setShowComposeModal(false)}
        title="✉️ Soạn thông báo email"
        actions={
          <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'space-between' }}>
            <button className="btn btn-ghost" onClick={handlePreview} disabled={isSending}>
              👁 Preview
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowComposeModal(false)}
                disabled={isSending}
              >
                Hủy
              </button>
              <button className="btn btn-primary" onClick={handleSend} disabled={isSending}>
                {isSending ? 'Đang gửi...' : '🚀 Gửi Email'}
              </button>
            </div>
          </div>
        }
      >
        <div style={{ maxWidth: 600 }}>
          {formError && (
            <div
              style={{
                marginBottom: 12,
                color: '#f87171',
                fontSize: 13,
                padding: '8px 12px',
                background: 'rgba(239,68,68,0.1)',
                borderRadius: 6,
              }}
            >
              ⚠️ {formError}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Subject <span style={{ color: '#f87171' }}>*</span>
            </label>
            <input
              className="input"
              type="text"
              placeholder="Tiêu đề email..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Target</label>
            <select
              className="input"
              value={target}
              onChange={(e) => setTarget(e.target.value as NotificationTarget)}
            >
              <option value="all">Tất cả users</option>
              <option value="free">Free tier</option>
              <option value="basic">Basic tier</option>
              <option value="premium">Premium tier</option>
              <option value="specific">Một email cụ thể</option>
            </select>
          </div>

          {target === 'specific' && (
            <div
              style={{
                marginTop: 12,
                padding: '12px 14px',
                background: 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.3)',
                borderRadius: 8,
              }}
            >
              <label className="form-label" style={{ marginBottom: 6 }}>
                📧 Địa chỉ email nhận <span style={{ color: '#f87171' }}>*</span>
              </label>
              <input
                type="email"
                className="input"
                value={specificEmail}
                onChange={(e) => setSpecificEmail(e.target.value)}
                placeholder="vd: phuhuynh@gmail.com"
                autoFocus
                style={{ width: '100%' }}
              />
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-3)' }}>
                Email sẽ được gửi đến đúng 1 địa chỉ này.
              </div>
            </div>
          )}

          <div className="form-group">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label className="form-label" style={{ margin: 0 }}>
                Nội dung email <span style={{ color: '#f87171' }}>*</span>
              </label>
              {/* Mode tabs */}
              <div style={{ display: 'flex', gap: 2, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 2 }}>
                {(['plain', 'html'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setEmailMode(mode)}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'var(--font-sans)',
                      background: emailMode === mode ? 'var(--accent)' : 'transparent',
                      color: emailMode === mode ? '#fff' : 'var(--text-3)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {mode === 'plain' ? 'Plain Text' : 'HTML'}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              className="input"
              style={{
                minHeight: 200,
                resize: 'vertical',
                fontFamily: emailMode === 'html' ? 'var(--font-mono)' : 'var(--font-sans)',
                fontSize: emailMode === 'html' ? 12.5 : 14,
              }}
              placeholder={
                emailMode === 'plain'
                  ? 'Nội dung email (plain text)...'
                  : '<p>Nội dung HTML. Bạn có thể dùng tag <b>, <a href>, <br>...</p>'
              }
              value={body}
              onChange={e => setBody(e.target.value)}
            />
            {/* Inline preview */}
            {showInlinePreview && body.trim() && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Preview</span>
                  <button onClick={() => setShowInlinePreview(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 14 }}>✕</button>
                </div>
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  background: '#fff',
                }}>
                  {emailMode === 'html' ? (
                    <iframe
                      srcDoc={`<!DOCTYPE html><html><body style="font-family:sans-serif;padding:16px;color:#111;">${body}</body></html>`}
                      style={{ width: '100%', height: 220, border: 'none' }}
                      sandbox="allow-same-origin"
                      title="Email preview"
                    />
                  ) : (
                    <pre style={{ padding: 16, margin: 0, fontSize: 13, color: '#111', whiteSpace: 'pre-wrap', fontFamily: 'sans-serif' }}>{body}</pre>
                  )}
                </div>
              </div>
            )}
          </div>

          {target !== 'specific' && (
            <div className="form-group">
              <label className="form-label">Giới hạn gửi tối đa</label>
              <input
                className="input"
                type="number"
                min={1}
                max={10000}
                value={limitSend}
                onChange={(e) => setLimitSend(parseInt(e.target.value) || 500)}
                style={{ width: 140 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-3)' }}>
                Số email tối đa sẽ được gửi trong một lần
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
